/**
 * EventTypePill — colour-coded event-type badge (b13e B4).
 *
 * Restores the old per-event-type pill colours the trainer flagged as missing
 * (purple PvP, red Max, orange Raid, green Community Day, …). Each category maps
 * to a single `--event-color-*` accent token from tokens.css; the pill derives
 * its tinted fill + border from that accent via color-mix, so light/dark mode is
 * handled entirely by the token (the .dark block brightens each hue).
 */
import type { GameEvent } from "@/lib/data";
import {
  Swords,
  Zap,
  Star,
  Sparkles,
  Egg,
  FlaskConical,
  Globe2,
  Calendar as CalendarIcon,
  Trophy,
  Ribbon,
  Layers,
  type LucideIcon,
} from "lucide-react";

type EventCategory = GameEvent["category"];

interface TypeMeta {
  label: string;
  /** CSS var name (without var()) for the accent colour. */
  token: string;
  icon: LucideIcon;
}

export const TYPE_META: Record<EventCategory, TypeMeta> = {
  pvp:             { label: "PvP",           token: "--event-color-pvp",       icon: Trophy },
  league:          { label: "League",        token: "--event-color-pvp",       icon: Trophy },
  "max-battle":    { label: "Max",           token: "--event-color-max",       icon: Zap },
  "max-monday":    { label: "Max Monday",    token: "--event-color-max",       icon: Zap },
  raid:            { label: "Raid",          token: "--event-color-raid",      icon: Swords },
  "raid-day":      { label: "Raid Day",      token: "--event-color-raid",      icon: Swords },
  "raid-hour":     { label: "Raid Hour",     token: "--event-color-raid",      icon: Swords },
  "community-day": { label: "Community Day", token: "--event-color-community", icon: Star },
  spotlight:       { label: "Spotlight",     token: "--event-color-spotlight", icon: Sparkles },
  "hatch-day":     { label: "Hatch Day",     token: "--event-color-hatch",     icon: Egg },
  research:        { label: "Research",      token: "--event-color-research",  icon: FlaskConical },
  "go-fest":       { label: "GO Fest",       token: "--event-color-gofest",    icon: Globe2 },
  "go-pass":       { label: "GO Pass",       token: "--event-color-research",  icon: Ribbon },
  season:          { label: "Season",        token: "--event-color-event",     icon: Layers },
  event:           { label: "Event",         token: "--event-color-event",     icon: CalendarIcon },
};

export const DEFAULT_META: TypeMeta = {
  label: "Event",
  token: "--event-color-default",
  icon: CalendarIcon,
};

/** Shared accessor so HeroCard and the pill draw from the same category map. */
export function eventTypeMeta(category: EventCategory): TypeMeta {
  return TYPE_META[category] ?? DEFAULT_META;
}

export function EventTypePill({
  category,
  className,
}: {
  category: EventCategory;
  className?: string;
}) {
  const meta = eventTypeMeta(category);
  const Icon = meta.icon;
  const accent = `var(${meta.token})`;
  return (
    <span
      data-testid={`event-type-pill-${category}`}
      className={`inline-flex items-center gap-1 rounded-[var(--radius-pill)] px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] whitespace-nowrap select-none ${className ?? ""}`}
      style={{
        color: accent,
        background: `color-mix(in srgb, ${accent} 14%, transparent)`,
        border: `1px solid color-mix(in srgb, ${accent} 38%, transparent)`,
      }}
    >
      <Icon className="w-3 h-3" />
      {meta.label}
    </span>
  );
}
